import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useFetcher } from 'react-router';
import { toast } from 'sonner';
import { useActivitySensor } from '@archipro-website/react-hooks';
import {
    CONVERSATION_API,
    type loader,
} from '~/routes/remix-api.chat.conversation';
import {
    CONVERSATION_MESSAGE_API,
    type loader as messageLoader,
} from '~/routes/remix-api.chat.message';
import {
    CREATE_CONVERSATION_MESSAGE_API,
    type action as createMessageAction,
} from '~/routes/remix-api.chat.create-conversation-message';
import {
    MARK_CONVERSATION_READ_API,
    type action as markConversationReadAction,
} from '~/routes/remix-api.chat.mark-conversation-read';
import {
    MARK_CONVERSATION_UNREAD_API,
    type action as markConversationUnreadAction,
} from '~/routes/remix-api.chat.mark-conversation-unread';
import {
    REACT_MESSAGE_API,
    type action as reactMessageAction,
} from '~/routes/remix-api.chat.react-message';
import {
    DELETE_MESSAGE_API,
    type action as deleteMessageAction,
} from '~/routes/remix-api.chat.delete-message';
import { TOAST_CLOSE_BUTTON } from '~/utils/toast';
import { useUserLoggedIn } from '~/modules/user';
import type {
    Conversation,
    Message,
    Participant,
    TextOrFileMessage,
} from '../types';
import {
    buildMentionLookup,
    convertToDeletedMessage,
    convertToReactedMessage,
    createOptimisticMessage,
    getChatParticipantsExcludingSelf,
    mergeConversations,
    revokeOptimisticBlobUrls,
    type MentionLookup,
} from '../utils';
import { useConversationCache } from '../hooks/use-conversation-cache';
import { useRealTimeChat } from '../hooks/use-real-time-chat';

interface ConversationContextValue {
    /**
     * The conversation ID. This is `0` if no conversation is set (e.g. new
     * conversation), or a conversation cannot be found for the given participants.
     */
    conversationID: number;
    /**
     * The professional ID if this conversation was opened from the company inbox
     * and is being viewed from the perspective of the professional. If `null`,
     * the personal inbox context will be used.
     */
    professionalID: number | null;
    /**
     * The conversation details. This is the fetched conversation details, or
     * falls back to the given partial conversation when the load is still in flight.
     */
    conversation: Conversation | undefined;
    /**
     * The messages in the conversation ordered by earliest first. Includes
     * optimistic messages that are not yet in the conversation.
     */
    messages: Message[];
    /**
     * The self participant in the conversation. This is the participant that is
     * the current logged-in user or the professional if in a company inbox context.
     */
    selfParticipant: Participant | null;
    /**
     * The identity of the self participant in the conversation.
     */
    selfIdentity: Participant['Identity'] | null;
    /**
     * The other participants in this conversation, excluding the self participant.
     */
    otherParticipants: Participant[];
    /**
     * Lookup used to render and compose participant mentions in this conversation.
     */
    mentionLookup: MentionLookup;
    /**
     * There are more (older) messages to load.
     */
    hasMoreMessages: boolean;
    /**
     * The current logged-in user is a participant in the conversation. In a
     * company inbox context, this means the company is a professional participant.
     */
    isParticipant: boolean;
    /**
     * The conversation is currently being loaded.
     */
    isLoading: boolean;
    /**
     * The create message fetcher is submitting.
     */
    isSubmitting: boolean;
    /**
     * The mark-as-read or mark-as-unread fetcher is not idle.
     */
    isMarkAsReadBusy: boolean;
    /**
     * Refetch the conversation details.
     * @param replaceMessages Replace the existing messages with the fetched ones,
     *   instead of being merged.
     */
    refetch: (replaceMessages?: boolean) => void;
    /**
     * Load more (older) messages by fetching the next page. Merges the new
     * messages with the existing ones.
     */
    loadMore: () => void;
    /**
     * Create a new message in the conversation.
     */
    createMessage: (input: {
        message?: string;
        attachments?: File[];
        replyTo?: TextOrFileMessage;
    }) => void;
    /**
     * Mark the conversation as read.
     */
    markAsRead: () => void;
    /**
     * Mark the conversation as unread.
     */
    markAsUnread: () => void;
    /**
     * Toggle an emoji reaction on a message.
     */
    toggleMessageReaction: (messageID: number, reaction: string) => void;
    /**
     * Delete a message from the conversation.
     */
    deleteMessage: (messageID: number) => void;
}

const ConversationContext = createContext<ConversationContextValue | null>(
    null,
);

/**
 * Provides conversation state and actions to descendant chat components.
 */
export function ConversationProvider({
    children,
    conversationID,
    professionalID,
    participantMemberIDs = [],
    participantProfessionalIDs = [],
    conversation: initialConversation,
    isPollingDisabled = false,
}: {
    children: React.ReactNode;
    /**
     * The conversation ID. If set to `0`, and no participant IDs are provided,
     * this hook will be disabled and the conversation will not be fetched.
     */
    conversationID: number;
    /**
     * The professional ID if this conversation was opened from the company inbox
     * and is being viewed from the perspective of the professional. If `null`,
     * the personal inbox context will be used.
     */
    professionalID: number | null;
    /**
     * The member IDs of the other participants, used to fetch the conversation by
     * participant set. When both `conversationID > 0` and participant IDs are
     * provided, `conversationID` takes precedence.
     */
    participantMemberIDs?: number[];
    /**
     * The professional IDs of the other participants, used to fetch the conversation
     * by participant set.
     */
    participantProfessionalIDs?: number[];
    /**
     * Optional partial/initial conversation (e.g. from the inbox list summary)
     * to show immediately while the fetch is still in flight.
     */
    conversation?: Conversation;
    /**
     * Disable conversation polling. Defaults to `false`.
     */
    isPollingDisabled?: boolean;
}) {
    const hasId = conversationID > 0;
    const hasParticipants =
        participantMemberIDs.length > 0 ||
        participantProfessionalIDs.length > 0;
    const { user, isLoggedIn } = useUserLoggedIn();
    const pageActive = useActivitySensor();
    const {
        getCachedConversation,
        getCachedConversationByParticipants,
        cacheConversation,
    } = useConversationCache();
    const processedDataRef = useRef<typeof fetcher.data | null>(null);
    const createMessageProcessedDataRef = useRef<
        typeof createMessageFetcher.data | null
    >(null);
    const isReplaceMessages = useRef(false);
    const [loadedConversation, setLoadedConversation] =
        useState<Conversation | null>(() => {
            if (hasId) {
                return (
                    getCachedConversation(conversationID, professionalID) ??
                    null
                );
            }
            if (hasParticipants) {
                return (
                    getCachedConversationByParticipants(
                        user.ID,
                        participantMemberIDs,
                        participantProfessionalIDs,
                    ) ?? null
                );
            }
            return null;
        });
    const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);
    const fetcher = useFetcher<typeof loader>();
    const messageFetcher = useFetcher<typeof messageLoader>();
    const createMessageFetcher = useFetcher<typeof createMessageAction>();
    const markAsReadFetcher = useFetcher<typeof markConversationReadAction>();
    const processedMarkAsReadDataRef = useRef<
        typeof markAsReadFetcher.data | null
    >(null);
    const markAsUnreadFetcher =
        useFetcher<typeof markConversationUnreadAction>();
    const processedMarkAsUnreadDataRef = useRef<
        typeof markAsUnreadFetcher.data | null
    >(null);
    const reactFetcher = useFetcher<typeof reactMessageAction>();
    const deleteFetcher = useFetcher<typeof deleteMessageAction>();
    const isDisabled = (!hasId && !hasParticipants) || !isLoggedIn;
    const conversation = useMemo(() => {
        return loadedConversation ?? initialConversation;
    }, [loadedConversation, initialConversation]);
    const effectiveConversationID = hasId
        ? conversationID
        : conversation
          ? Number(conversation.ID)
          : 0;
    const messages = useMemo(() => {
        const serverMessages = conversation?.Messages.nodes ?? [];
        return [...optimisticMessages, ...serverMessages];
    }, [conversation, optimisticMessages]);
    const selfParticipant = useMemo(() => {
        if (professionalID && professionalID > 0) {
            return (
                conversation?.Participants.find(
                    ({ Identity }) =>
                        Identity.__typename === 'Professional' &&
                        Identity.ID === professionalID,
                ) ?? null
            );
        }
        if (!isLoggedIn) {
            return null;
        }
        return (
            conversation?.Participants.find(
                ({ Identity }) =>
                    Identity.__typename === 'Member' && Identity.ID === user.ID,
            ) ?? null
        );
    }, [conversation, isLoggedIn, user.ID, professionalID]);
    const selfIdentity = useMemo(() => {
        return selfParticipant?.Identity ?? null;
    }, [selfParticipant]);
    const otherParticipants = useMemo(() => {
        return getChatParticipantsExcludingSelf(
            conversation,
            isLoggedIn ? user.ID : 0,
            professionalID,
        );
    }, [conversation, isLoggedIn, user.ID, professionalID]);
    const mentionLookup = useMemo(() => {
        return buildMentionLookup(conversation?.Participants ?? []);
    }, [conversation]);
    const isParticipant = useMemo(() => {
        if (!conversation) {
            return true;
        }
        return !!selfParticipant;
    }, [conversation, selfParticipant]);
    /** The self participant is on the professional side of an enquiry conversation. */
    const isEnquiryProfessionalSide = useMemo(() => {
        return (
            conversation?.__typename === 'EnquiryConversation' &&
            (selfIdentity?.__typename === 'Professional' ||
                (selfParticipant?.__typename === 'EnquiryParticipant' &&
                    selfParticipant.IsCollaborator))
        );
    }, [conversation, selfParticipant, selfIdentity]);
    const apiUrl = useMemo(() => {
        const url = new URL(CONVERSATION_API, window.location.origin);
        if (hasId) {
            url.searchParams.set('id', String(conversationID));
            if (professionalID && professionalID > 0) {
                url.searchParams.set('professionalID', String(professionalID));
            }
        } else if (hasParticipants) {
            for (const participantID of participantMemberIDs) {
                url.searchParams.append(
                    'participantMemberIDs',
                    String(participantID),
                );
            }
            for (const participantID of participantProfessionalIDs) {
                url.searchParams.append(
                    'participantProfessionalIDs',
                    String(participantID),
                );
            }
        }
        return `${url.pathname}${url.search}`;
    }, [
        conversationID,
        professionalID,
        hasId,
        hasParticipants,
        participantMemberIDs,
        participantProfessionalIDs,
    ]);
    const loadMoreUrl = useMemo(() => {
        if (
            !conversation?.Messages.nodes.length ||
            !conversation.Messages.pageInfo?.hasNextPage
        ) {
            return null;
        }
        const url = new URL(CONVERSATION_API, window.location.origin);
        url.searchParams.set('id', conversation.ID);
        if (professionalID && professionalID > 0) {
            url.searchParams.set('professionalID', String(professionalID));
        }
        url.searchParams.set(
            'beforeID',
            conversation.Messages.nodes.at(-1)!.ID,
        );
        return `${url.pathname}${url.search}`;
    }, [conversation, professionalID]);
    const { isConnected } = useRealTimeChat({
        onMessage: (message) => {
            if (isDisabled) return;
            if (message.conversation_id === effectiveConversationID) {
                if (
                    message.type === 'chat/message' ||
                    message.type === 'chat/update'
                ) {
                    // Refetch the conversation if updated.
                    refetch();
                } else if (
                    message.type === 'chat/read' &&
                    (String(message.participant_id) === selfParticipant?.ID ||
                        (message.is_enquiry_professional_side &&
                            isEnquiryProfessionalSide))
                ) {
                    // Update the unread count when marked as read/unread from another tab.
                    setLoadedConversation((prev) => {
                        const unreadMessageCount = message.unread_message_count;
                        if (
                            !prev ||
                            prev.UnreadMessageCount === unreadMessageCount
                        ) {
                            return prev;
                        }
                        const newConversation: Conversation = {
                            ...prev,
                            UnreadMessageCount: unreadMessageCount,
                        };
                        cacheConversation(newConversation, professionalID);
                        return newConversation;
                    });
                } else if (message.type === 'chat/message/updated') {
                    // Just update the message if a message was updated.
                    messageFetcher.load(
                        `${CONVERSATION_MESSAGE_API}?id=${message.message_id}`,
                    );
                }
            }
            if (
                message.type === 'chat/migrate' &&
                (message.conversation_id === effectiveConversationID ||
                    message.original_conversation_id ===
                        effectiveConversationID)
            ) {
                // Replace the messages when messages are migrated because
                // messages may be removed or inserted in between other messages.
                refetch(true);
            }
        },
    });

    const refetch = useCallback(
        (replaceMessages: boolean = false) => {
            if (isDisabled) return;
            isReplaceMessages.current = replaceMessages;
            fetcher.load(apiUrl);
        },
        [isDisabled, fetcher, apiUrl],
    );

    const loadMore = useCallback(() => {
        if (fetcher.state !== 'idle' || !loadMoreUrl) {
            return;
        }
        fetcher.load(loadMoreUrl);
    }, [fetcher, loadMoreUrl]);

    const createMessage = useCallback(
        ({
            message,
            attachments,
            replyTo,
        }: {
            message?: string;
            attachments?: File[];
            replyTo?: TextOrFileMessage;
        }) => {
            if (isDisabled || effectiveConversationID <= 0 || !isLoggedIn) {
                return;
            }
            const optimisticMessage = createOptimisticMessage({
                conversationID: effectiveConversationID,
                professionalID,
                message,
                attachments,
                replyTo,
                conversation,
                user,
            });
            // TODO: tracking
            const formData = new FormData();
            formData.append('conversationID', String(effectiveConversationID));
            formData.append('clientMessageID', optimisticMessage.ID);
            if (professionalID && professionalID > 0) {
                formData.append('professionalID', String(professionalID));
            }
            if (message) {
                formData.append('message', message);
            }
            if (attachments?.length) {
                attachments.forEach((attachment) => {
                    formData.append('attachments', attachment);
                });
            }
            if (replyTo) {
                formData.append('replyToID', String(replyTo.ID));
            }
            createMessageFetcher.submit(formData, {
                action: CREATE_CONVERSATION_MESSAGE_API,
                method: 'POST',
                encType: attachments?.length
                    ? 'multipart/form-data'
                    : undefined,
            });
            setOptimisticMessages((prev) => [optimisticMessage, ...prev]);
        },
        [
            isDisabled,
            isLoggedIn,
            effectiveConversationID,
            professionalID,
            conversation,
            user,
            createMessageFetcher,
        ],
    );

    const replaceOptimisticMessage = useCallback(
        (clientMessageID: string, sentMessage: Message) => {
            setOptimisticMessages((messages) => {
                let isReplaced = false;
                return messages.map((message) => {
                    if (
                        !isReplaced &&
                        message.isOptimistic &&
                        message.ID === clientMessageID
                    ) {
                        revokeOptimisticBlobUrls([message]);
                        isReplaced = true;
                        return {
                            ...sentMessage,
                            isOptimistic: false,
                        };
                    }
                    return message;
                });
            });
        },
        [],
    );

    const removeOptimisticMessage = useCallback((clientMessageID: string) => {
        setOptimisticMessages((messages) => {
            const removedMessages = messages.filter(
                (message) =>
                    message.isOptimistic && message.ID === clientMessageID,
            );
            if (removedMessages.length) {
                revokeOptimisticBlobUrls(removedMessages);
            }
            return messages.filter(
                (message) =>
                    !message.isOptimistic || message.ID !== clientMessageID,
            );
        });
    }, []);

    const markAsRead = useCallback(() => {
        if (isDisabled || effectiveConversationID <= 0) return;
        markAsReadFetcher.submit(
            {
                conversationID: effectiveConversationID,
                professionalID,
            },
            {
                action: MARK_CONVERSATION_READ_API,
                method: 'POST',
                encType: 'application/json',
            },
        );
        // Optimistic UI.
        setLoadedConversation((prev) => {
            if (!prev || prev.UnreadMessageCount <= 0) {
                return prev;
            }
            const newConversation: Conversation = {
                ...prev,
                UnreadMessageCount: 0,
            };
            cacheConversation(newConversation, professionalID);
            return newConversation;
        });
    }, [
        isDisabled,
        effectiveConversationID,
        professionalID,
        markAsReadFetcher,
        cacheConversation,
    ]);

    const markAsUnread = useCallback(() => {
        if (isDisabled || effectiveConversationID <= 0) return;
        markAsUnreadFetcher.submit(
            {
                conversationID: effectiveConversationID,
                professionalID,
            },
            {
                action: MARK_CONVERSATION_UNREAD_API,
                method: 'POST',
                encType: 'application/json',
            },
        );
        // Optimistic UI.
        setLoadedConversation((prev) => {
            if (!prev || prev.UnreadMessageCount > 0) {
                return prev;
            }
            const newConversation: Conversation = {
                ...prev,
                UnreadMessageCount: 1,
            };
            cacheConversation(newConversation, professionalID);
            return newConversation;
        });
    }, [
        isDisabled,
        effectiveConversationID,
        professionalID,
        markAsUnreadFetcher,
        cacheConversation,
    ]);

    const updateMessage = useCallback(
        (message: Message) => {
            setLoadedConversation((prev) => {
                if (!prev?.Messages.nodes.some((m) => m.ID === message.ID)) {
                    return prev;
                }
                const updatedConversation = {
                    ...prev,
                    Messages: {
                        ...prev.Messages,
                        // @ts-expect-error
                        nodes: [message],
                    },
                } satisfies Conversation as Conversation;
                // Need to use this function to update the message with the correct types.
                const merged = mergeConversations(prev, updatedConversation);
                if (merged) {
                    cacheConversation(merged, professionalID);
                }
                return merged;
            });
        },
        [professionalID, cacheConversation],
    );

    const toggleMessageReaction = useCallback(
        (messageID: number, reaction: string) => {
            const message = messages.find((m) => m.ID === String(messageID));
            const isRemove =
                !!message &&
                'Reactions' in message &&
                message.Reactions.some(
                    (r) =>
                        r.Reaction === reaction &&
                        r.Participant.ID === selfParticipant?.ID,
                );
            reactFetcher.submit(
                { messageID, reaction, isRemove, professionalID },
                {
                    action: REACT_MESSAGE_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
            if (message) {
                updateMessage(
                    convertToReactedMessage(message, selfParticipant, reaction),
                );
            }
        },
        [
            reactFetcher,
            messages,
            selfParticipant,
            professionalID,
            updateMessage,
        ],
    );

    const deleteMessage = useCallback(
        (messageID: number) => {
            deleteFetcher.submit(
                { messageID, professionalID },
                {
                    action: DELETE_MESSAGE_API,
                    method: 'POST',
                    encType: 'application/json',
                },
            );
            const deletedMessage = messages.find(
                (m) => m.ID === String(messageID),
            );
            if (deletedMessage) {
                updateMessage(convertToDeletedMessage(deletedMessage));
            }
        },
        [deleteFetcher, messages, professionalID, updateMessage],
    );

    // Trigger a fetch whenever the lookup key (apiUrl) changes.
    useEffect(() => {
        if (isDisabled) {
            setLoadedConversation(null);
            setOptimisticMessages((prev) => {
                revokeOptimisticBlobUrls(prev);
                return [];
            });
            processedDataRef.current = null;
            return;
        }
        fetcher.load(apiUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDisabled, apiUrl]);

    // Process fetcher data and set conversation.
    useEffect(() => {
        if (isDisabled) return;
        if (
            fetcher.data &&
            fetcher.state === 'idle' &&
            fetcher.data !== processedDataRef.current
        ) {
            if (isReplaceMessages.current) {
                setLoadedConversation(fetcher.data.conversation);
                if (fetcher.data.conversation) {
                    cacheConversation(
                        fetcher.data.conversation,
                        professionalID,
                    );
                }
            } else {
                // The new fetcher data might be a different page, so merge the
                // messages instead of replacing the conversation.
                setLoadedConversation((prev) => {
                    const merged = mergeConversations(
                        prev,
                        fetcher.data!.conversation,
                    );
                    if (merged) {
                        cacheConversation(merged, professionalID);
                    }
                    return merged;
                });
            }
            setOptimisticMessages((prev) => {
                revokeOptimisticBlobUrls(prev);
                return [];
            });
            processedDataRef.current = fetcher.data;
            isReplaceMessages.current = false;
        }
    }, [
        isDisabled,
        professionalID,
        fetcher.data,
        fetcher.state,
        cacheConversation,
    ]);

    // Use the cached conversation if it exists.
    useEffect(() => {
        if (isDisabled) return;
        const cachedConversation = hasId
            ? getCachedConversation(conversationID, professionalID)
            : hasParticipants
              ? getCachedConversationByParticipants(
                    user.ID,
                    participantMemberIDs,
                    participantProfessionalIDs,
                )
              : undefined;
        if (cachedConversation) {
            setLoadedConversation(cachedConversation);
        }
    }, [
        isDisabled,
        hasId,
        conversationID,
        professionalID,
        hasParticipants,
        user.ID,
        participantMemberIDs,
        participantProfessionalIDs,
        getCachedConversation,
        getCachedConversationByParticipants,
    ]);

    // When the lookup key (`conversationID`, `participantMemberIDs`, or
    // `participantProfessionalIDs`) changes, reset the conversation if the
    // currently loaded one no longer matches.
    useEffect(() => {
        if (isDisabled) return;
        setLoadedConversation((prev) => {
            if (!prev) return prev;
            if (hasId) {
                return Number(prev.ID) === conversationID ? prev : null;
            }
            if (hasParticipants) {
                const isParticipantsSameLength =
                    prev.Participants.length ===
                    participantMemberIDs.length +
                        participantProfessionalIDs.length +
                        1;
                const isParticipantsMatch =
                    isParticipantsSameLength &&
                    isLoggedIn &&
                    prev.Participants.some(
                        ({ Identity }) =>
                            Identity.__typename === 'Member' &&
                            Identity.ID === user.ID,
                    ) &&
                    participantMemberIDs.every((pid) =>
                        prev.Participants.some(
                            ({ Identity }) =>
                                Identity.__typename === 'Member' &&
                                Identity.ID === pid,
                        ),
                    ) &&
                    participantProfessionalIDs.every((pid) =>
                        prev.Participants.some(
                            ({ Identity }) =>
                                Identity.__typename === 'Professional' &&
                                Identity.ID === pid,
                        ),
                    );
                return isParticipantsMatch ? prev : null;
            }
            return null;
        });
    }, [
        isDisabled,
        hasId,
        conversationID,
        hasParticipants,
        isLoggedIn,
        user.ID,
        participantMemberIDs,
        participantProfessionalIDs,
    ]);

    // Poll every 5 minutes if the websocket is connected or the page is
    // inactive; fall back to 30 seconds when the websocket is disconnected and
    // the user is active.
    useEffect(() => {
        if (isDisabled || isPollingDisabled) {
            return;
        }
        const pollingIntervalMs = isConnected || !pageActive ? 300_000 : 30_000;
        const interval = setInterval(() => {
            refetch();
        }, pollingIntervalMs);
        return () => clearInterval(interval);
    }, [isDisabled, isPollingDisabled, isConnected, pageActive, refetch]);

    // Update the message after fetching from the server.
    useEffect(() => {
        if (messageFetcher.data && messageFetcher.state === 'idle') {
            updateMessage(messageFetcher.data.message);
        }
    }, [messageFetcher, updateMessage]);

    // Update the optimistic messages on create message mutation success.
    useEffect(() => {
        if (
            createMessageFetcher.data &&
            createMessageFetcher.state !== 'submitting' &&
            createMessageFetcher.data !== createMessageProcessedDataRef.current
        ) {
            const { response, clientMessageID } = createMessageFetcher.data;
            if (
                response.__typename === 'TextMessage' ||
                response.__typename === 'FileAttachmentMessage'
            ) {
                // When we get the result of the create message mutation, replace
                // the optimistic message with the real message.
                replaceOptimisticMessage(clientMessageID, response);
            } else {
                removeOptimisticMessage(clientMessageID);
                toast.error(response.Message, TOAST_CLOSE_BUTTON);
            }
            createMessageProcessedDataRef.current = createMessageFetcher.data;
        }
    }, [
        createMessageFetcher,
        replaceOptimisticMessage,
        removeOptimisticMessage,
    ]);

    // Update the conversation status on mark as read mutation success.
    useEffect(() => {
        if (
            markAsReadFetcher.data &&
            markAsReadFetcher.state !== 'submitting' &&
            markAsReadFetcher.data !== processedMarkAsReadDataRef.current
        ) {
            const updatedConversation = markAsReadFetcher.data.conversation;
            setLoadedConversation((prev) => {
                if (!prev) {
                    return prev;
                }
                const newConversation: Conversation = {
                    ...prev,
                    UnreadMessageCount: updatedConversation.UnreadMessageCount,
                };
                cacheConversation(newConversation, professionalID);
                return newConversation;
            });
            processedMarkAsReadDataRef.current = markAsReadFetcher.data;
        }
    }, [professionalID, markAsReadFetcher, cacheConversation]);

    // Update the conversation status on mark as unread mutation success.
    useEffect(() => {
        if (
            markAsUnreadFetcher.data &&
            markAsUnreadFetcher.state !== 'submitting' &&
            markAsUnreadFetcher.data !== processedMarkAsUnreadDataRef.current
        ) {
            const updatedConversation = markAsUnreadFetcher.data.conversation;
            setLoadedConversation((prev) => {
                if (!prev) {
                    return prev;
                }
                const newConversation: Conversation = {
                    ...prev,
                    UnreadMessageCount: updatedConversation.UnreadMessageCount,
                };
                cacheConversation(newConversation, professionalID);
                return newConversation;
            });
            processedMarkAsUnreadDataRef.current = markAsUnreadFetcher.data;
        }
    }, [professionalID, markAsUnreadFetcher, cacheConversation]);

    // Update message when react message mutation finishes.
    useEffect(() => {
        if (reactFetcher.data && reactFetcher.state !== 'submitting') {
            const response = reactFetcher.data.response;
            if (
                response.__typename === 'TextMessage' ||
                response.__typename === 'FileAttachmentMessage' ||
                response.__typename === 'DeletedMessage'
            ) {
                updateMessage(response);
            } else {
                toast.error('Failed to react to message', TOAST_CLOSE_BUTTON);
            }
        }
    }, [reactFetcher, updateMessage]);

    // Update message when delete message mutation finishes.
    useEffect(() => {
        if (deleteFetcher.data && deleteFetcher.state !== 'submitting') {
            const response = deleteFetcher.data.response;
            if (response.__typename === 'DeletedMessage') {
                updateMessage(response);
            } else {
                toast.error('Failed to delete message', TOAST_CLOSE_BUTTON);
            }
        }
    }, [deleteFetcher, updateMessage]);

    const value = useMemo<ConversationContextValue>(
        () => ({
            conversationID: effectiveConversationID,
            professionalID,
            conversation,
            messages,
            selfParticipant,
            selfIdentity,
            otherParticipants,
            mentionLookup,
            hasMoreMessages:
                conversation?.Messages.pageInfo?.hasNextPage ?? false,
            isParticipant,
            isLoading: fetcher.state !== 'idle',
            isSubmitting: createMessageFetcher.state === 'submitting',
            isMarkAsReadBusy:
                markAsReadFetcher.state !== 'idle' ||
                markAsUnreadFetcher.state !== 'idle',
            refetch,
            loadMore,
            createMessage,
            markAsRead,
            markAsUnread,
            toggleMessageReaction,
            deleteMessage,
        }),
        [
            effectiveConversationID,
            professionalID,
            conversation,
            messages,
            selfParticipant,
            selfIdentity,
            otherParticipants,
            mentionLookup,
            isParticipant,
            fetcher.state,
            createMessageFetcher.state,
            markAsReadFetcher.state,
            markAsUnreadFetcher.state,
            refetch,
            loadMore,
            createMessage,
            markAsRead,
            markAsUnread,
            toggleMessageReaction,
            deleteMessage,
        ],
    );

    return (
        <ConversationContext.Provider value={value}>
            {children}
        </ConversationContext.Provider>
    );
}

/**
 * Read the conversation context. Must be used within a `ConversationProvider`,
 * otherwise it will throw an error.
 */
export function useConversation(): ConversationContextValue {
    const ctx = useContext(ConversationContext);
    if (!ctx) {
        throw new Error(
            'useConversation must be used within a ConversationProvider',
        );
    }
    return ctx;
}
